import { featureCollection, bbox } from '@turf/turf';
import { Position, FeatureCollection, Feature, Geometry } from 'geojson';
import mapboxgl from 'mapbox-gl';
import { OrientationOptions } from 'screens/Applications&WorkOrders/Hooks/useOperationPdf';

const ACCESS_TOKEN = "pk.eyJ1Ijoic3RyaWRlcmVuZ2luZWVyaW5nIiwiYSI6ImNsdWs5ZG1vNzA2azYyam1scHljMjNyc3kifQ.YBj-8NtAHftqXroxqmbxXw";
const TOLERANCE = 0.001;

interface GeoJsonFeature {
  type: 'Feature';
  properties: {
    'marker-size': string;
    'marker-color': string;
  };
  geometry: {
    type: 'MultiPoint';
    coordinates: Position[];
  };
}

export const createGeoJsonFeature = (
  coordinates: Position[]
): GeoJsonFeature => ({
  type: 'Feature',
  properties: {
    'marker-size': 'xs',
    'marker-color': '#18A04A'
  },
  geometry: {
    type: 'MultiPoint',
    coordinates
  }
});

const fillGeoJsonOnMap = (
  map: mapboxgl.Map,
  geoJson: FeatureCollection,
  actualPixelRatio: number
): Promise<string> => {
  const bboxGeoJSON = bbox(geoJson).slice(0, 4) as [
    number,
    number,
    number,
    number
  ];
  const fillColor = '#31B4F2';
  const fillOpacity = 0.37;
  const lineColor = '#31B4F2';
  const lineWidth = 1;

  const setupMapLayers = () => {
    map.addSource('geojson', {
      type: 'geojson',
      data: geoJson as any
    });

    map.addLayer({
      id: 'geojson',
      type: 'fill',
      source: 'geojson',
      paint: {
        'fill-color': fillColor,
        'fill-opacity': fillOpacity
      }
    });

    map.addLayer({
      id: 'outline',
      type: 'line',
      source: 'geojson',
      paint: {
        'line-color': lineColor,
        'line-width': lineWidth
      }
    });

    map.fitBounds(bboxGeoJSON, { padding: 20 });
  };

  const captureMapImage = (resolve: (value: string) => void) => {
    map.once('idle', () => {
      resolve(map.getCanvas().toDataURL('image/png'));
      Object.defineProperty(globalThis, 'devicePixelRatio', {
        get() {
          return actualPixelRatio;
        }
      });
    });
  };

  return new Promise((resolve, reject) => {
    map.on('error', reject);

    map.on('load', () => {
      try {
        setupMapLayers();
        map.once('render', () => captureMapImage(resolve));
      } catch (error) {
        reject(error);
      }
    });
  });
};

export const createMapUrl = async (
  geoJson: FeatureCollection,
  overrideStyles: Partial<CSSStyleDeclaration> = {}
): Promise<string> => {
  mapboxgl.accessToken = ACCESS_TOKEN!;

  const actualPixelRatio = globalThis.devicePixelRatio;
  Object.defineProperty(globalThis, 'devicePixelRatio', {
    get() {
      return 150 / 96;
    }
  });

  const container = document.createElement('div');
  const mapStyle = 'mapbox://styles/mapbox/satellite-streets-v11';

  const containerStyle: Partial<CSSStyleDeclaration> = {
    zIndex: '100',
    visibility: 'hidden',
    position: 'absolute',
    top: '0',
    height: '100%',
    width: '100%',
    ...overrideStyles
  };
  // Set container style before creating map to ensure correct size
  Object.assign(container.style, containerStyle);
  // Append to body so Mapbox GL can render properly
  document.body.appendChild(container);

  const map = new mapboxgl.Map({
    container,
    style: mapStyle,
    fadeDuration: 0,
    interactive: false,
    preserveDrawingBuffer: true
  });

  try {
    const imageDataUrl = await fillGeoJsonOnMap(map, geoJson, actualPixelRatio);
    // Clean up: remove container and map
    map.remove();
    container.remove();
    return imageDataUrl;
  } catch (error) {
    // Clean up on error
    map.remove();
    if (container.parentNode) {
      container.remove();
    }
    throw error;
  }
};

export const calculateDynamicTolerance = (
  baseTolerance: number,
  featureCount: number,
  scalingFactor: number = TOLERANCE
): number => {
  return baseTolerance / featureCount ** scalingFactor;
};

export const convertFeaturesToMapUri = async (
  features?: Feature[],
  orientation: OrientationOptions = 'portrait'
): Promise<string> => {
  if (!features?.length) {
    return '';
  }
  const geoJsonData = featureCollection(features);
  const mapUri = await createMapUrl(
    geoJsonData,
    orientation === 'portrait'
      ? {
          height: '300px',
          width: '260px'
        }
      : {
          height: '300px',
          width: '360px'
        }
  );

  return mapUri;
};

export const buildFeature = (geometry: Geometry): Feature => ({
  type: 'Feature',
  properties: {
    stroke: '#31B4F2',
    'stroke-width': 1,
    fill: '#31B4F2',
    'fill-opacity': 0.37
  },
  geometry
});

export const convertFeatureToIconUri = async (
  geometry?: Geometry
): Promise<string> => {
  if (!geometry) {
    return '';
  }
  const feature = buildFeature(geometry);
  const geoJsonData = featureCollection([feature]);
  const iconUri = await createMapUrl(geoJsonData, {
    height: '24px',
    width: '24px'
  });

  return iconUri;
};

export const convertGeometryToSvgPath = (geometry?: Geometry): string => {
  if (!geometry || !('coordinates' in geometry) || !geometry.coordinates) {
    return '';
  }

  // Simple bounding box calculation to fit the geometry in the viewBox
  let coords: any[];

  if (geometry.type === 'Polygon') {
    coords = geometry.coordinates[0] as any[];
  } else if (geometry.type === 'MultiPolygon') {
    [coords] = geometry.coordinates[0] as any[];
  } else {
    coords = [];
  }

  if (!coords || coords.length === 0) {
    return '';
  }

  const lngs = coords.map((c: any) => c[0]);
  const lats = coords.map((c: any) => c[1]);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);

  const width = maxLng - minLng;
  const height = maxLat - minLat;

  // Add padding
  const padding = Math.max(width, height) * 0.1;

  // Simple mercator-like projection to SVG coordinates (34x34 viewBox)
  const project = (lng: number, lat: number): [number, number] => {
    const x = ((lng - minLng + padding) / (width + 2 * padding)) * 34;
    const y = 34 - ((lat - minLat + padding) / (height + 2 * padding)) * 34; // Flip Y axis
    return [x, y];
  };

  // Generate SVG path
  const pathParts = coords.map((coord: any, i: number) => {
    const [x, y] = project(coord[0], coord[1]);
    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  });

  const pathData = `${pathParts.join(' ')} Z`;

  return pathData;
};
